import type { ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

type Phase = 'idle' | 'out' | 'in'

interface TransitionContextValue {
    phase: Phase
    navigate: (href: string) => void
}

interface TransitionProviderProps {
    children: ReactNode
}

const OUT_MS = 420
const IN_MS = 500

const TransitionContext = createContext<TransitionContextValue>({
    phase: 'idle',
    navigate: () => undefined
})

const stripHash = (value: string): string => value.split('#')[0] || '/'

export const TransitionProvider = ({ children }: TransitionProviderProps): ReactNode => {
    const router = useRouter()
    const reduced = useReducedMotion()
    const [phase, setPhase] = useState<Phase>('idle')
    const busy = useRef(false)
    const timers = useRef<Array<number>>([])

    const later = useCallback((callback: () => void, ms: number) => {
        timers.current.push(window.setTimeout(callback, ms))
    }, [])

    useEffect(() => {
        const pending = timers.current

        return () => pending.forEach(id => window.clearTimeout(id))
    }, [])

    const navigate = useCallback((href: string) => {
        if (busy.current) return

        const target = stripHash(href)
        const current = stripHash(router.asPath)

        if (target === current) {
            if (href.includes('#')) {
                void router.push(href)
                return
            }

            window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
            return
        }

        if (reduced) {
            void router.push(href)
            return
        }

        busy.current = true
        setPhase('out')

        later(() => {
            router.push(href)
                .catch(() => false)
                .finally(() => {
                    setPhase('in')
                    later(() => {
                        setPhase('idle')
                        busy.current = false
                    }, IN_MS)
                })
        }, OUT_MS)
    }, [router, reduced, later])

    const value = useMemo(() => ({ phase, navigate }), [phase, navigate])

    return (
        <TransitionContext.Provider value={value}>
            {children}

            <div className='cut' data-phase={phase} aria-hidden='true'>
                <div className='cut-dim' />
                <div className='cut-line' />
            </div>
        </TransitionContext.Provider>
    )
}

export const usePageTransition = (): TransitionContextValue => useContext(TransitionContext)
